import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp, formatCurrency } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, CalendarIcon, TrendingUp, TrendingDown } from "lucide-react";
import { format, isWithinInterval, parseISO } from "date-fns";

const FILTER_MODES = [
  { label: "All Transactions", value: "all" },
  { label: "Current Cycle", value: "current" },
  { label: "Custom Date Range", value: "custom" },
];

export default function CategoryDetail() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { categories, transactions, cycleConfig } = useApp();
  
  const [filterMode, setFilterMode] = useState("all");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const category = categories.find(c => c.id === categoryId);

  // Get current cycle date range
  const currentCycleRange = useMemo(() => {
    if (cycleConfig?.customStart && cycleConfig?.customEnd) {
      return {
        start: new Date(cycleConfig.customStart),
        end: new Date(cycleConfig.customEnd),
        label: `${format(new Date(cycleConfig.customStart), "dd MMM")} → ${format(new Date(cycleConfig.customEnd), "dd MMM")}`
      };
    }
    return null;
  }, [cycleConfig]);

  // Filter transactions for this category
  const filteredTransactions = useMemo(() => {
    // First filter by category
    let filtered = transactions.filter(t => t.categoryId === categoryId);
    
    // Then apply date filter
    if (filterMode === "current" && currentCycleRange) {
      filtered = filtered.filter(t => {
        const txDate = new Date(t.dateISO);
        return txDate >= currentCycleRange.start && txDate <= currentCycleRange.end;
      });
    } else if (filterMode === "custom") {
      filtered = filtered.filter(t => {
        const txDate = parseISO(t.dateISO);
        return isWithinInterval(txDate, { start: startDate, end: endDate });
      });
    }
    // "all" mode shows all transactions for this category
    
    return filtered.sort((a, b) => new Date(b.dateISO) - new Date(a.dateISO));
  }, [transactions, categoryId, filterMode, currentCycleRange, startDate, endDate]);

  const totalAmount = useMemo(() => {
    return filteredTransactions.reduce((sum, t) => sum + t.amount, 0);
  }, [filteredTransactions]);

  if (!category) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-stone-500">
        <p>Category not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/categories')}>Back to Categories</Button>
      </div>
    );
  }

  const isIncome = category.type === "income";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="category-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/categories')} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${isIncome ? "bg-emerald-100" : "bg-rose-100"}`}>
            {isIncome ? <TrendingUp className="w-6 h-6 text-emerald-600" /> : <TrendingDown className="w-6 h-6 text-rose-600" />}
          </div>
          <div>
            <h2 className="font-heading text-3xl text-stone-900">{category.name}</h2>
            <p className="text-stone-500 capitalize">{category.type} Category</p>
          </div>
        </div>
      </div>

      {/* Filter Card */}
      <Card className="p-4 bg-white rounded-2xl border border-stone-200/60">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <CalendarIcon className="w-4 h-4 text-stone-500" />
            <span className="text-sm font-medium text-stone-600">Filter:</span>
            
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="Select filter" /></SelectTrigger>
              <SelectContent>
                {FILTER_MODES.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Show current cycle info */}
          {filterMode === "current" && currentCycleRange && (
            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <p className="text-sm text-indigo-800">
                Showing transactions from: <strong>{currentCycleRange.label}</strong>
              </p>
            </div>
          )}

          {/* Custom Date Range */}
          {filterMode === "custom" && (
            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-stone-100">
              <span className="text-sm text-stone-500">From:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px]">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(startDate, "dd MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={(date) => date && setStartDate(date)} initialFocus />
                </PopoverContent>
              </Popover>
              <span className="text-sm text-stone-500">To:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px]">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(endDate, "dd MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={(date) => date && setEndDate(date)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className={`p-6 rounded-2xl border ${isIncome ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
            Total {isIncome ? "Earned" : "Spent"}
          </p>
          <p className={`font-body font-bold text-3xl tracking-tighter ${isIncome ? "text-emerald-700" : "text-rose-700"}`}>
            {formatCurrency(totalAmount)}
          </p>
        </Card>
        <Card className="p-6 bg-stone-50 rounded-2xl border border-stone-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Transactions</p>
          <p className="font-body font-bold text-3xl tracking-tighter text-stone-900">{filteredTransactions.length}</p>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card className="bg-white rounded-2xl border border-stone-200/60 overflow-hidden">
        <div className="p-4 border-b border-stone-100">
          <h3 className="font-semibold text-stone-900">
            Transactions ({filteredTransactions.length})
          </h3>
        </div>
        
        {filteredTransactions.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((tx) => (
                  <TableRow key={tx.id} className="hover:bg-stone-50">
                    <TableCell className="font-mono text-sm">
                      {new Date(tx.dateISO).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${isIncome ? "text-emerald-700" : "text-rose-700"}`}>
                      {isIncome ? "+" : "-"}{formatCurrency(tx.amount)}
                    </TableCell>
                    <TableCell className="text-stone-500 max-w-[300px] truncate">{tx.notes || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-stone-400">
            <p className="font-medium">No transactions found</p>
            <p className="text-sm">Try changing the filter</p>
          </div>
        )}
      </Card>
    </div>
  );
}
